<template>
    <SettingsPage
        :schema="schema"
        :initial-value="currentSettings"
        :loading="loading"
        :updating="updating"
        title="Branding Settings"
        :description="description"
        @save="save"
    >
        <template #preview="{ value }">
            <h3>Email Preview</h3>

            <ActionEmailPreview :value="value" :defaults="currentSettings" />

            <div class="SettingsPage__note">
                Note: The text displayed in the email above is for preview purposes only.
                The actual text used in your email is controlled on a per-template basis.
            </div>
        </template>

        <template #default="{ fields }">
            <div class="SettingsPage__formRow">
                <JsonSchemaCustomFormElement :field="fields.displayName" />
            </div>

            <div class="SettingsPage__formRow">
                <JsonSchemaCustomFormElement :field="fields.wordmarkUrl" />
            </div>

            <div class="SettingsPage__formRow">
                <JsonSchemaCustomFormElement :field="fields.primaryColor" />
            </div>

            <div class="SettingsPage__formRow">
                <JsonSchemaCustomFormElement :field="fields.secondaryColor" />
            </div>
        </template>
    </SettingsPage>
</template>

<script lang="ts">
    import { Component, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { JsonSchemaCustomFormElement } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import { useNotificationsConfigurationStore } from '@/modules/notifications-configuration/store';
    import { usePageLayoutStore } from '@/modules/page-layout/store';

    import ActionEmailPreview from '../components/ActionEmailPreview.vue';
    import SettingsPage from '../components/SettingsPage.vue';
    import { buildBrandingSettingsSchema } from '../schemas/buildBrandingSettingsSchema';
    import { BrandingConfiguration, NotificationsConfiguration, NotificationsConfigurationState } from '../types';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';

    @Component({
        components: {
            SettingsPage,
            ActionEmailPreview,
            JsonSchemaCustomFormElement,
        },
    })
    export default class BrandingSettings extends Vue {
        private get notificationsConfigurationStore() {
            return useNotificationsConfigurationStore();
        }

        private get pageLayoutStore() {
            return usePageLayoutStore();
        }

        private schema = buildBrandingSettingsSchema();
        private description = 'Customize how your organization appears in emails and across the Evident network. Set your company name, logo, and primary color.';

        private created(): void {
            this.pageLayoutStore.resetPageLayout();
        }

        private get rpName(): string {
            return this.$route.params.rpId as string;
        }

        private get state(): NotificationsConfigurationState {
            return this.notificationsConfigurationStore.$state;
        }

        private get status(): OperationStatus {
            return this.state.loadSettingsStatus[this.rpName] || OperationStatus.uninitialized;
        }

        private get loading(): boolean {
            return ![ OperationStatus.success, OperationStatus.error ].includes(this.status);
        }

        private get updateStatus(): OperationStatus {
            return this.state.updateSettingsStatus[this.rpName] || OperationStatus.uninitialized;
        }

        private get updating(): boolean {
            return this.updateStatus === OperationStatus.loading;
        }

        private get currentSettings(): NotificationsConfiguration | null {
            return this.state.settings[this.rpName] || null;
        }

        @Watch('loading', { immediate: true })
        private onLoadingChange(): void {
            this.pageLayoutStore.setLoading(this.loading);
        }

        @Watch('rpName', { immediate: true })
        private async onRpChange(newVal: string, oldVal: string): Promise<void> {
            if (newVal && newVal !== oldVal) {
                await useRbacStore().validateRbac([ RbacFeature.displayAndEmailBrandingSettings ]);
            }
        }

        private async save(settings: BrandingConfiguration): Promise<void> {
            await this.notificationsConfigurationStore.updateSettings({
                rpName: this.rpName,
                settings,
            });

            if (this.updateStatus === OperationStatus.success) {
                useSnackbarStore().displaySnackbar({
                    message: 'Branding settings updated successfully.',
                    success: true,
                });
            }
        }

        @Watch('status', { immediate: true })
        private loadSettings(): void {
            if (this.status === OperationStatus.uninitialized) {
                this.notificationsConfigurationStore.loadSettings({ rpName: this.rpName });
            }
        }
    }
</script>
